/**
 * Input — themed text input. Replaces the ~128 ad-hoc inline-style <input>s
 * scattered across pages with one control that has a consistent border, focus
 * ring, and error state (all from theme.css variables).
 *
 * Pass `label`/`hint`/`error` to get the standard Field layout; omit them and
 * the bare <input> is returned so it drops in anywhere a raw <input> was used.
 * `style` targets the input; `fieldStyle` targets the Field wrapper.
 */
import { forwardRef, useState, type InputHTMLAttributes, type ReactNode, type CSSProperties } from 'react';
import { Field } from './Field';
import { controlBase, focusedRing, errorBorder } from './field-styles';

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: ReactNode;
  hint?: ReactNode;
  error?: ReactNode;
  /** Style for the surrounding Field wrapper (only when a label/hint/error is shown). */
  fieldStyle?: CSSProperties;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { label, hint, error, fieldStyle, style, onFocus, onBlur, id, ...rest },
  ref,
) {
  const [focused, setFocused] = useState(false);
  const control = (
    <input
      ref={ref}
      id={id}
      style={{
        ...controlBase,
        ...(focused ? focusedRing : null),
        ...(error != null ? errorBorder : null),
        ...style,
      }}
      onFocus={(e) => {
        setFocused(true);
        onFocus?.(e);
      }}
      onBlur={(e) => {
        setFocused(false);
        onBlur?.(e);
      }}
      {...rest}
    />
  );
  if (label == null && hint == null && error == null) return control;
  return (
    <Field label={label} hint={hint} error={error} htmlFor={id} style={fieldStyle}>
      {control}
    </Field>
  );
});
